import React, { useState } from 'react';
import { X, Building2, Mail, Phone, User, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

const LeadCaptureModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    nome_empresa: '',
    cnpj: '',
    email: '',
    telefone: '',
    nome_contato: '',
    observacoes: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 14) {
      return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return value;
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
    }
    return value;
  };

  const handleInputChange = (field, value) => {
    let formattedValue = value;

    if (field === 'cnpj') {
      formattedValue = formatCNPJ(value);
    } else if (field === 'telefone') {
      formattedValue = formatPhone(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }));

    // Limpar erro do campo
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome_empresa.trim()) {
      newErrors.nome_empresa = 'Nome da empresa é obrigatório';
    }

    const cnpjNumeros = formData.cnpj.replace(/\D/g, '');
    if (!cnpjNumeros) {
      newErrors.cnpj = 'CNPJ é obrigatório';
    } else if (cnpjNumeros.length !== 14) {
      newErrors.cnpj = 'CNPJ deve ter 14 dígitos';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    const telefoneNumeros = formData.telefone.replace(/\D/g, '');
    if (!telefoneNumeros) {
      newErrors.telefone = 'Telefone é obrigatório';
    } else if (telefoneNumeros.length < 10) {
      newErrors.telefone = 'Telefone inválido';
    }

    if (!formData.nome_contato.trim()) {
      newErrors.nome_contato = 'Nome do contato é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const createLead = httpsCallable(functions, 'createLead');
      const result = await createLead({
        nome_empresa: formData.nome_empresa,
        cnpj: formData.cnpj.replace(/\D/g, ''),
        email: formData.email.toLowerCase(),
        telefone: formData.telefone,
        nome_contato: formData.nome_contato,
        observacoes: formData.observacoes || undefined
      });

      if (result.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          // Reset form
          setFormData({
            nome_empresa: '',
            cnpj: '',
            email: '',
            telefone: '',
            nome_contato: '',
            observacoes: ''
          });
          setSuccess(false);
        }, 3000);
      } else {
        throw new Error(result.data.message || 'Erro ao cadastrar lead');
      }
    } catch (err) {
      console.error('Erro ao cadastrar lead:', err);
      setError(err.message || 'Erro ao enviar seus dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Building2 className="h-8 w-8" />
              <div>
                <h2 className="text-2xl font-bold">Começar Agora</h2>
                <p className="text-blue-100 text-sm">Cadastre sua empresa e receba uma demonstração gratuita</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
              disabled={loading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-12">
              <div className="flex justify-center mb-4">
                <div className="bg-green-100 rounded-full p-4">
                  <CheckCircle className="h-16 w-16 text-green-600" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Cadastro Recebido!</h3>
              <p className="text-gray-600 mb-4">
                Obrigado pelo seu interesse no QualiManager!
              </p>
              <p className="text-gray-600">
                Nossa equipe entrará em contato em breve para apresentar nossa solução.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nome da Empresa */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Empresa *
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.nome_empresa}
                      onChange={(e) => handleInputChange('nome_empresa', e.target.value)}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md ${
                        errors.nome_empresa ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ex: Indústria XYZ Ltda"
                      disabled={loading}
                    />
                  </div>
                  {errors.nome_empresa && (
                    <p className="text-red-500 text-xs mt-1">{errors.nome_empresa}</p>
                  )}
                </div>

                {/* CNPJ */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CNPJ *
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.cnpj}
                      onChange={(e) => handleInputChange('cnpj', e.target.value)}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md ${
                        errors.cnpj ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="00.000.000/0000-00"
                      maxLength={18}
                      disabled={loading}
                    />
                  </div>
                  {errors.cnpj && (
                    <p className="text-red-500 text-xs mt-1">{errors.cnpj}</p>
                  )}
                </div>

                {/* Telefone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.telefone}
                      onChange={(e) => handleInputChange('telefone', e.target.value)}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md ${
                        errors.telefone ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="(11) 98765-4321"
                      maxLength={15}
                      disabled={loading}
                    />
                  </div>
                  {errors.telefone && (
                    <p className="text-red-500 text-xs mt-1">{errors.telefone}</p>
                  )}
                </div>

                {/* Nome do Contato */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Contato *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.nome_contato}
                      onChange={(e) => handleInputChange('nome_contato', e.target.value)}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md ${
                        errors.nome_contato ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Seu nome completo"
                      disabled={loading}
                    />
                  </div>
                  {errors.nome_contato && (
                    <p className="text-red-500 text-xs mt-1">{errors.nome_contato}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md ${
                        errors.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="contato@empresa.com.br"
                      disabled={loading}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Observações */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações (Opcional)
                  </label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(e) => handleInputChange('observacoes', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Conte-nos um pouco sobre sua necessidade..."
                    rows={3}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>🎁 Demonstração Gratuita:</strong> Nossa equipe entrará em contato para agendar uma demonstração personalizada do QualiManager e entender melhor as necessidades da sua empresa.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5" />
                      <span>Solicitar Contato</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureModal;

